import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const UpgradeRecommendationCard = ({ 
  recommendation, 
  onViewOptions = () => {},
  onAddToWishlist = () => {},
  onViewDetails = () => {},
  className = ""
}) => {
  const [isWishlisted, setIsWishlisted] = useState(recommendation?.isWishlisted || false);

  const handleWishlistToggle = () => {
    setIsWishlisted(!isWishlisted);
    onAddToWishlist(recommendation, !isWishlisted);
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'text-error';
      case 'medium': return 'text-warning';
      case 'low': return 'text-success';
      default: return 'text-muted-foreground';
    }
  };

  const getPriorityBg = (priority) => {
    switch (priority) {
      case 'high': return 'bg-error/10';
      case 'medium': return 'bg-warning/10';
      case 'low': return 'bg-success/10';
      default: return 'bg-muted';
    }
  };

  const getDifficultyIcon = (difficulty) => {
    switch (difficulty) {
      case 'easy': return 'CheckCircle';
      case 'medium': return 'AlertCircle';
      case 'hard': return 'XCircle';
      default: return 'HelpCircle';
    }
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'easy': return 'var(--color-success)';
      case 'medium': return 'var(--color-warning)';
      case 'hard': return 'var(--color-error)';
      default: return 'var(--color-muted-foreground)';
    }
  };

  return (
    <div className={`bg-card border border-border rounded-lg overflow-hidden hover:shadow-md transition-shadow ${className}`}>
      {/* Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center space-x-2 mb-1">
              <h3 className="font-semibold text-foreground">{recommendation?.component}</h3>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityBg(recommendation?.priority)} ${getPriorityColor(recommendation?.priority)}`}>
                {recommendation?.priority} priority
              </span>
            </div>
            <p className="text-sm text-muted-foreground">{recommendation?.category}</p>
          </div>
          <button
            onClick={handleWishlistToggle}
            className="p-2 hover:bg-muted rounded-full transition-colors interactive-scale"
            aria-label={isWishlisted ? "Remove from wishlist" : "Add to wishlist"}
          >
            <Icon 
              name="Heart" 
              size={18} 
              color={isWishlisted ? "var(--color-error)" : "var(--color-muted-foreground)"}
              className={isWishlisted ? "fill-current" : ""}
            />
          </button>
        </div>
      </div>
      {/* Current vs Recommended */}
      <div className="p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          {/* Current Component */}
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-muted-foreground">Current</h4>
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-muted rounded-md flex items-center justify-center overflow-hidden">
                {recommendation?.current?.image ? (
                  <Image 
                    src={recommendation?.current?.image} 
                    alt={recommendation?.current?.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <Icon name="Package" size={20} color="var(--color-muted-foreground)" />
                )}
              </div>
              <div className="flex-1">
                <div className="text-sm font-medium text-foreground">{recommendation?.current?.name}</div>
                <div className="text-xs text-muted-foreground">{recommendation?.current?.specs}</div>
              </div>
            </div>
          </div>

          {/* Recommended Component */}
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-muted-foreground">Recommended</h4>
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-muted rounded-md flex items-center justify-center overflow-hidden">
                {recommendation?.recommended?.image ? (
                  <Image 
                    src={recommendation?.recommended?.image} 
                    alt={recommendation?.recommended?.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <Icon name="Package" size={20} color="var(--color-accent)" />
                )}
              </div>
              <div className="flex-1">
                <div className="text-sm font-medium text-foreground">{recommendation?.recommended?.name}</div>
                <div className="text-xs text-muted-foreground">{recommendation?.recommended?.specs}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Benefits */}
        <div className="mb-4">
          <h4 className="text-sm font-medium text-foreground mb-2">Performance Benefits</h4>
          <div className="space-y-1">
            {recommendation?.benefits?.map((benefit, index) => (
              <div key={index} className="flex items-center space-x-2 text-sm">
                <Icon name="ArrowUp" size={14} color="var(--color-success)" />
                <span className="text-muted-foreground">{benefit}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Cost and Difficulty */}
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <div className="text-sm text-muted-foreground">Estimated Cost</div>
            <div className="text-lg font-bold text-foreground">${recommendation?.cost?.toLocaleString()}</div>
            {recommendation?.priceRange && (
              <div className="text-xs text-muted-foreground">
                Range: ${recommendation?.priceRange?.min} - ${recommendation?.priceRange?.max}
              </div>
            )}
          </div>
          <div>
            <div className="text-sm text-muted-foreground">Installation</div>
            <div className="flex items-center space-x-1">
              <Icon 
                name={getDifficultyIcon(recommendation?.difficulty)} 
                size={16} 
                color={getDifficultyColor(recommendation?.difficulty)}
              />
              <span className="text-sm font-medium text-foreground capitalize">
                {recommendation?.difficulty}
              </span>
            </div>
            {recommendation?.installTime && (
              <div className="text-xs text-muted-foreground">
                ~{recommendation?.installTime}
              </div>
            )}
          </div>
        </div>

        {/* Compatibility Status */}
        <div className="mb-4">
          <div className="flex items-center space-x-2 p-2 rounded-md bg-muted/50">
            <Icon 
              name={recommendation?.compatibility === 'perfect' ? 'CheckCircle' : 
                    recommendation?.compatibility === 'good' ? 'AlertCircle' : 'XCircle'} 
              size={16} 
              color={recommendation?.compatibility === 'perfect' ? 'var(--color-success)' : 
                     recommendation?.compatibility === 'good' ? 'var(--color-warning)' : 'var(--color-error)'}
            />
            <span className="text-sm text-foreground">
              {recommendation?.compatibility === 'perfect' ? 'Perfect Compatibility' :
               recommendation?.compatibility === 'good' ? 'Good Compatibility' : 'Requires Adapter'}
            </span>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex space-x-2">
          <Button
            variant="default"
            size="sm"
            onClick={() => onViewOptions(recommendation)}
            className="flex-1"
            iconName="ExternalLink"
            iconPosition="right"
          >
            View Options
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onViewDetails(recommendation)}
            iconName="Info"
            iconPosition="left"
          >
            Details
          </Button>
        </div>
      </div>
      {/* Footer with additional info */}
      {recommendation?.vendorCount && (
        <div className="px-4 py-2 bg-muted/30 border-t border-border">
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>Available from {recommendation?.vendorCount} vendors</span>
            {recommendation?.avgRating && (
              <div className="flex items-center space-x-1">
                <Icon name="Star" size={12} color="var(--color-warning)" className="fill-current" />
                <span>{recommendation?.avgRating}/5</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default UpgradeRecommendationCard;